/**
 * Pluggable CSPRNG seam for W3C trace-id / span-id generation.
 *
 * The generator must NEVER fall back to `Math.random` (non-cryptographic). On
 * the web the default source is the platform Web Crypto (`crypto.getRandomValues`),
 * read lazily at call time so importing this package never throws on a runtime
 * without a `crypto` global (e.g. React-Native / Hermes). The React-Native app
 * wiring registers `expo-crypto` via {@link configureTraceRandomSource} in a
 * later phase; until then, a crypto-less runtime simply emits no `traceparent`.
 */
/**
 * Returns `byteLength` cryptographically-secure random bytes, or `null` when it
 * cannot provide entropy — in which case no `traceparent` is emitted (never a
 * `Math.random` fallback).
 */
export type RandomBytesSource = (byteLength: number) => Uint8Array | null;

let randomBytesSource: RandomBytesSource | undefined;

/**
 * Register a CSPRNG (e.g. `expo-crypto` on React-Native). Overrides the web
 * default. Pass `undefined` to restore the default Web Crypto source.
 */
export const configureTraceRandomSource = (source: RandomBytesSource | undefined): void => {
  randomBytesSource = source;
};

/**
 * Return `byteLength` cryptographically-secure random bytes, or `null` when no
 * secure source is available (never `Math.random`). The source is resolved at
 * call time so a later {@link configureTraceRandomSource} takes effect and a
 * crypto-less runtime degrades gracefully instead of throwing.
 */
export const getSecureRandomBytes = (byteLength: number): Uint8Array | null => {
  if (randomBytesSource) {
    return randomBytesSource(byteLength);
  }

  const webCrypto = globalThis.crypto;
  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    return webCrypto.getRandomValues(new Uint8Array(byteLength));
  }

  return null;
};
